/**
 * TagChipEditor — Push 70b (D-198 wallet collapse, 2026-07-17).
 *
 * Multi-select chip input primitive for the free-form user-tags array
 * introduced in Migration 0155 (sys__account_user.tags TEXT[]). Powers
 * the People list inline editor, PersonDetail Tags field, and the
 * BulkCapByTagModal filter.
 *
 * Shape (Jira-style):
 *   ┌──────────────────────────────────────────────────────┐
 *   │  [sales ×] [west ×] [pilot ×]   type to add…         │
 *   ├──────────────────────────────────────────────────────┤
 *   │  ▸ sales                                             │
 *   │  ▸ marketing                                         │
 *   │  ▸ finance                                           │
 *   └──────────────────────────────────────────────────────┘
 *
 * Behaviors:
 *   - Chip pills render for each active tag with an × to remove
 *   - Trailing input for typing new tag values
 *   - Typeahead dropdown filters `suggestions` by current input (case-insensitive)
 *   - Enter / Tab / Comma commits the input as a new chip
 *   - Selecting a suggestion adds that tag (preserves first-typed casing)
 *   - Backspace with empty input removes the last chip
 *   - Escape clears the current input
 *   - Duplicates blocked (case-insensitive compare)
 *   - Empty / whitespace-only tags rejected
 *   - 64-char per-tag cap matches the server's sanitizeTagList in
 *     /api/admin/people/[id]/tags.ts
 *
 * Aria semantics:
 *   - Input has role="combobox" with aria-controls pointing at the listbox
 *   - Suggestions rendered as role="listbox" > role="option"
 *   - Chip × buttons are aria-labeled "Remove {tag}"
 *
 * Style DNA: matches .modal-title + form-input tokens. Chip pill is
 * ~24px tall, rounded 999px, 4-10px horizontal padding, 4px gap between
 * chips, 6px gap between chip row and input. Muted × icon at 10px.
 * Focus state = teal outline on the outer container (matches other form
 * inputs).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IconClose } from './Icon.tsx';

export interface TagChipEditorProps {
  /** Current tag values. */
  value: string[];
  /** Fires with the new tag array on any change (add / remove). */
  onChange: (next: string[]) => void;
  /** Typeahead source. Case-insensitive dedupe is done client-side; casing
   *  from this list wins on suggestion select. */
  suggestions?: string[];
  placeholder?: string;
  disabled?: boolean;
  /** Optional cap on the number of tags. Undefined = unlimited. */
  maxTags?: number;
  className?: string;
  /** ARIA label for screen readers when there's no visible <label>. */
  ariaLabel?: string;
}

/** Case-insensitive membership check. */
function containsCI(list: readonly string[], candidate: string): boolean {
  const key = candidate.toLowerCase();
  return list.some((t) => t.toLowerCase() === key);
}

/** Trim + strip trailing commas. Empty result signals "not addable." */
function sanitize(raw: string): string {
  let out = raw.trim();
  // Trailing commas from Tab/Enter/Paste split — quietly strip.
  while (out.endsWith(',')) out = out.slice(0, -1).trim();
  return out;
}

export function TagChipEditor({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Add tag…',
  disabled = false,
  maxTags,
  className,
  ariaLabel,
}: TagChipEditorProps) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useMemo(
    () => `tag-typeahead-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const atCap = maxTags !== undefined && value.length >= maxTags;

  // Filter suggestions: exclude already-selected tags (case-insensitive),
  // then prefix-match against current input. When the input is empty,
  // show every unselected suggestion.
  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    const selectedLower = new Set(value.map((t) => t.toLowerCase()));
    return suggestions
      .filter((s) => !selectedLower.has(s.toLowerCase()))
      .filter((s) => q === '' || s.toLowerCase().includes(q));
  }, [input, suggestions, value]);

  // Reset active suggestion index whenever the filtered list changes.
  useEffect(() => {
    setActiveSuggestion(filteredSuggestions.length > 0 ? 0 : -1);
  }, [filteredSuggestions.length]);

  const dropdownOpen = focused && filteredSuggestions.length > 0;

  const commit = useCallback(
    (raw: string) => {
      const clean = sanitize(raw);
      if (clean === '') return;
      if (clean.length > 64) return;
      if (containsCI(value, clean)) {
        setInput('');
        return;
      }
      if (atCap) return;
      onChange([...value, clean]);
      setInput('');
    },
    [atCap, onChange, value],
  );

  const remove = useCallback(
    (tag: string) => {
      const next = value.filter((t) => t !== tag);
      onChange(next);
    },
    [onChange, value],
  );

  const selectSuggestion = useCallback(
    (idx: number) => {
      const tag = filteredSuggestions[idx];
      if (tag === undefined) return;
      if (atCap) return;
      if (containsCI(value, tag)) return;
      onChange([...value, tag]);
      setInput('');
      // Keep focus so the admin can add another.
      inputRef.current?.focus();
    },
    [atCap, filteredSuggestions, onChange, value],
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      // Tab commits ONLY when there's typed text — otherwise let it move focus.
      if (e.key === 'Tab' && input.trim() === '') return;
      // Prefer selecting the highlighted suggestion when the dropdown is
      // open and the user pressed Enter/Tab (matches typeahead UX).
      if (
        dropdownOpen &&
        (e.key === 'Enter' || e.key === 'Tab') &&
        activeSuggestion >= 0
      ) {
        e.preventDefault();
        selectSuggestion(activeSuggestion);
        return;
      }
      e.preventDefault();
      commit(input);
      return;
    }
    if (e.key === 'Backspace' && input === '' && value.length > 0) {
      e.preventDefault();
      const last = value[value.length - 1]!;
      remove(last);
      return;
    }
    if (e.key === 'Escape') {
      if (input !== '') {
        e.preventDefault();
        setInput('');
      }
      return;
    }
    if (e.key === 'ArrowDown' && dropdownOpen) {
      e.preventDefault();
      setActiveSuggestion((prev) =>
        Math.min(prev + 1, filteredSuggestions.length - 1),
      );
      return;
    }
    if (e.key === 'ArrowUp' && dropdownOpen) {
      e.preventDefault();
      setActiveSuggestion((prev) => Math.max(prev - 1, 0));
      return;
    }
  }

  // Close the dropdown when focus leaves the whole editor (including
  // suggestion clicks — those are handled onMouseDown to fire before blur).
  useEffect(() => {
    function handleDocumentClick(e: MouseEvent) {
      if (containerRef.current === null) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  return (
    <div
      ref={containerRef}
      className={
        className !== undefined
          ? `tag-chip-editor ${className}`
          : 'tag-chip-editor'
      }
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        minHeight: 36,
        padding: '4px 6px',
        border: `1px solid ${
          focused ? 'var(--olytic-teal)' : 'var(--border)'
        }`,
        borderRadius: 6,
        background: disabled ? 'var(--olytic-obsidian-08)' : 'var(--olytic-white, #fff)',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 120ms ease',
      }}
      onClick={() => {
        if (!disabled) inputRef.current?.focus();
      }}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="tag-chip"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 6px 2px 8px',
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--olytic-obsidian)',
            background: 'var(--olytic-obsidian-08)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            lineHeight: 1.4,
          }}
        >
          <span>{tag}</span>
          {!disabled && (
            <button
              type="button"
              className="tag-chip-remove"
              aria-label={`Remove ${tag}`}
              onClick={(e) => {
                e.stopPropagation();
                remove(tag);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                padding: 0,
                background: 'transparent',
                border: 'none',
                borderRadius: 999,
                cursor: 'pointer',
                color: 'var(--fg-muted)',
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--border)';
                e.currentTarget.style.color = 'var(--olytic-obsidian)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--fg-muted)';
              }}
            >
              <IconClose size={10} />
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        placeholder={atCap ? '' : placeholder}
        disabled={disabled || atCap}
        aria-label={ariaLabel ?? 'Add tag'}
        role="combobox"
        aria-expanded={dropdownOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          dropdownOpen && activeSuggestion >= 0
            ? `${listboxId}-opt-${activeSuggestion}`
            : undefined
        }
        style={{
          flex: 1,
          minWidth: 100,
          padding: '4px 6px',
          fontSize: 13,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--olytic-obsidian)',
          fontFamily: 'inherit',
        }}
      />
      {dropdownOpen && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: 220,
            overflowY: 'auto',
            padding: 4,
            margin: 0,
            listStyle: 'none',
            background: 'var(--bg-elevated, var(--olytic-white, #fff))',
            border: '1px solid var(--border)',
            borderRadius: 6,
            boxShadow: 'var(--shadow-md, 0 6px 16px rgba(0,0,0,0.08))',
            zIndex: 20,
          }}
        >
          {filteredSuggestions.map((s, i) => (
            <li
              key={s}
              id={`${listboxId}-opt-${i}`}
              role="option"
              aria-selected={i === activeSuggestion}
              // Use mouseDown so we fire before input.onBlur (which would
              // close the dropdown before the click registers).
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(i);
              }}
              onMouseEnter={() => setActiveSuggestion(i)}
              style={{
                padding: '6px 10px',
                fontSize: 13,
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--olytic-obsidian)',
                background:
                  i === activeSuggestion
                    ? 'var(--olytic-obsidian-08)'
                    : 'transparent',
              }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
